import { format } from 'date-fns';
import React, { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldPath,
  get,
  UseFormClearErrors,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { ReverseVestingOnOption } from 'shared';
import { twMerge } from 'tailwind-merge';

import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { FormInput } from '../../../../components/input/index';
import { Switch } from '../../../../components/switch/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useRevealClip } from '../../../../hooks/use-reveal-clip.hook';
import { toNumber } from '../../../../utils/to-number.util';
import {
  getVestingScheduleErrors,
  isVestingScheduleMessage,
  VESTING_SCHEDULE_FIELDS,
} from '../../../../utils/vesting-schedule-errors.util';
import { FormSchema, ReverseVestingFormData } from '../event-form.validation';
import { ReverseVestingOnCombobox } from './reverse-vesting-on-combobox';

export type ReverseVestingFieldPath =
  | `companyIncorporation.issuances.${number}.reverseVesting`
  | `optionalStep.share-issuance.issuanceItems.${number}.reverseVesting`;

export type ReverseVestingFieldsProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  control: Control<FormSchema>;
  /** Freezes the toggle and every field — a terminated stakeholder's schedule cannot change. */
  disabled?: boolean;
  /** Shown on hover over the frozen block; omit for a plain disabled state with no explanation. */
  disabledReason?: ReactNode;
  issuedShares: number;
  namePrefix: ReverseVestingFieldPath;
  setFormData: UseFormSetValue<FormSchema>;
};

const rowLabelClassName = 'text-sm font-[450] text-gray-700';
const rowCaptionClassName = 'text-[10px] font-normal leading-4 text-gray-500';
const dividerClassName = 'h-[1px] w-full bg-gray-100';
/** Narrower than the 224px inputs above the toggle, which visually nests the schedule fields. */
const fieldWidthClassName = 'w-[192px]';

export const ReverseVestingFields: FC<ReverseVestingFieldsProps> = ({
  clearErrors,
  control,
  disabled = false,
  disabledReason,
  issuedShares,
  namePrefix,
  setFormData,
}) => {
  const reverseVesting = useWatch({
    control,
    name: namePrefix as FieldPath<FormSchema>,
  }) as ReverseVestingFormData | undefined;

  const isEnabled = Boolean(reverseVesting?.enabled);
  const isClipped = useRevealClip(isEnabled);

  const handleToggle = useCallback(
    (enabled: boolean) => {
      if (disabled) return;

      if (!enabled) {
        setFormData(namePrefix, undefined);
        clearErrors(namePrefix);
        return;
      }

      setFormData(namePrefix, { enabled: true, shares: issuedShares || undefined });
      clearErrors(namePrefix);
    },
    [clearErrors, disabled, issuedShares, namePrefix, setFormData],
  );

  const reverseVestedShares = toNumber(reverseVesting?.shares) ?? 0;
  const percentage = issuedShares > 0 ? (reverseVestedShares / issuedShares) * 100 : 0;
  // Over the issuance the field already reads "Amount cannot exceed Issued Shares"; a percentage
  // beside it (33,333.3%) only restates the mistake as a number nobody can use.
  const isPercentageShown = isEnabled && reverseVestedShares <= issuedShares;
  const { cliffMonths, durationMonths, frequencyMonths } = reverseVesting ?? {};
  const scheduleErrors = useMemo(
    () => getVestingScheduleErrors({ cliffMonths, durationMonths, frequencyMonths }),
    [cliffMonths, durationMonths, frequencyMonths],
  );

  const { errors } = useFormState({ control, name: namePrefix as FieldPath<FormSchema> });
  const fieldErrors = get(errors, namePrefix) as FieldErrors<ReverseVestingFormData> | undefined;

  // The inputs show `scheduleErrors`, but react-hook-form still holds the resolver's copy from the
  // last time each field was edited — fixing the frequency would leave "Duration should be a
  // multiple of frequency" on the untouched duration. Only schedule-rule messages are cleared.
  useEffect(() => {
    VESTING_SCHEDULE_FIELDS.forEach((field) => {
      if (scheduleErrors[field] || !isVestingScheduleMessage(fieldErrors?.[field]?.message)) return;
      clearErrors(`${namePrefix}.${field}` as FieldPath<FormSchema>);
    });
  }, [clearErrors, fieldErrors, namePrefix, scheduleErrors]);

  const toggle = (
    <Switch checked={isEnabled} disableChange disabled={disabled} onChange={handleToggle} />
  );

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-col gap-3">
        <div className={dividerClassName} />

        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className={rowLabelClassName}>
              <AppFormattedMessage id="REVERSE_VESTING" />
            </span>
            <span className={rowCaptionClassName}>
              <AppFormattedMessage id="REVERSE_VESTING_DESCRIPTION" />
            </span>
          </div>

          {/* Anchored on the toggle, not the row: the row spans the panel, so a row-anchored
              tooltip centres over the fields above instead of over the control it explains.
              344px is the width the design specifies, which is what wraps it onto two lines.

              The wrapper is load-bearing twice over. Tooltip clones its child to attach the hover
              ref and its own layout classes, which deform the Switch if applied to it directly —
              the wrapper absorbs them instead. And it has to be a div: Tooltip only shows
              unprompted for non-text children, so a span would gate the tooltip behind an
              ellipsis measurement that a control never satisfies. */}
          {disabled && disabledReason ? (
            <Tooltip className="w-[344px]" content={disabledReason}>
              <div className="w-fit shrink-0">{toggle}</div>
            </Tooltip>
          ) : (
            toggle
          )}
        </div>
      </div>

      <div
        className={twMerge(
          'grid grid-flow-row transition-all duration-200 ease-in-out',
          isEnabled ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className={twMerge(isClipped && 'overflow-hidden')}>
          <div className="flex w-full flex-col gap-3 pt-3" inert={!isEnabled || disabled}>
            <div className={dividerClassName} />

            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex flex-col">
                <span className={rowLabelClassName}>
                  <AppFormattedMessage id="SHARES" />
                </span>
                <span className={rowCaptionClassName}>
                  <AppFormattedMessage id="SUBJECT_TO_REVERSE_VESTING" />
                </span>
              </div>
              <div className={`flex flex-col gap-2 ${fieldWidthClassName}`}>
                <FormInput
                  control={control}
                  disabled={disabled}
                  name={`${namePrefix}.shares` as FieldPath<FormSchema>}
                  numberOnly
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  shouldFormatNumber
                  wrapperClassName="w-full"
                />

                {/* Fades in just behind the reveal (the section itself animates over 200ms) so the
                    caption arrives with the fields instead of snapping in at full strength, and
                    counts to its new share of the issuance rather than jumping per keystroke. */}
                <span
                  className={twMerge(
                    'text-xs font-[450] text-gray-600 transition-opacity duration-200 ease-in-out',
                    isPercentageShown ? 'opacity-100 delay-150' : 'opacity-0',
                  )}
                >
                  <AppFormattedMessage
                    id="REVERSE_VESTING_PERCENTAGE"
                    values={{
                      percentage: (
                        <AnimatedNumber
                          duration={250}
                          maximumFractionDigits={2}
                          value={percentage}
                        />
                      ),
                    }}
                  />
                </span>
              </div>
            </div>

            <div className="flex w-full items-center justify-between gap-4">
              <span className={rowLabelClassName}>
                <AppFormattedMessage id="START_DATE" />
              </span>
              <FormDatePicker
                disabled={disabled}
                hideIcon={disabled}
                inputValue={
                  reverseVesting?.startDate ? format(reverseVesting.startDate, 'dd/MM/yyyy') : ''
                }
                inputWrapperClassName={fieldWidthClassName}
                onSelect={(date) =>
                  setFormData(`${namePrefix}.startDate` as FieldPath<FormSchema>, date, {
                    shouldValidate: true,
                  })
                }
                placeholder={<AppFormattedMessage id="START_DATE" />}
                value={reverseVesting?.startDate}
                wrapperClassName={fieldWidthClassName}
              />
            </div>

            <div className="flex w-full items-center justify-between gap-4">
              <span className={rowLabelClassName}>
                <AppFormattedMessage id="VESTING_ON" />
              </span>
              <ReverseVestingOnCombobox
                disabled={disabled}
                onSelect={(option: ReverseVestingOnOption) =>
                  setFormData(`${namePrefix}.vestingOn` as FieldPath<FormSchema>, option, {
                    shouldValidate: true,
                  })
                }
                value={reverseVesting?.vestingOn}
                wrapperClassName={fieldWidthClassName}
              />
            </div>

            <div className="flex w-full items-center justify-between gap-4">
              <span className={rowLabelClassName}>
                <AppFormattedMessage id="DURATION" />
              </span>
              <FormInput
                control={control}
                disabled={disabled}
                errorMessage={scheduleErrors.durationMonths}
                name={`${namePrefix}.durationMonths` as FieldPath<FormSchema>}
                numberOnly
                placeholder={<AppFormattedMessage id="MONTHS" />}
                wrapperClassName={fieldWidthClassName}
              />
            </div>

            <div className="flex w-full items-center justify-between gap-4">
              <span className={rowLabelClassName}>
                <AppFormattedMessage id="VESTING_FREQUENCY" />
              </span>
              <FormInput
                control={control}
                disabled={disabled}
                errorMessage={scheduleErrors.frequencyMonths}
                name={`${namePrefix}.frequencyMonths` as FieldPath<FormSchema>}
                numberOnly
                placeholder={<AppFormattedMessage id="MONTHS" />}
                wrapperClassName={fieldWidthClassName}
              />
            </div>

            <div className="flex w-full items-center justify-between gap-4">
              <span className={rowLabelClassName}>
                <AppFormattedMessage id="CLIFF" />
              </span>
              <FormInput
                control={control}
                disabled={disabled}
                errorMessage={scheduleErrors.cliffMonths}
                name={`${namePrefix}.cliffMonths` as FieldPath<FormSchema>}
                numberOnly
                placeholder={<AppFormattedMessage id="MONTHS" />}
                wrapperClassName={fieldWidthClassName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
